/**
 * Module dependencies.
 */

import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover';
import { cn } from 'app/lib/utils';

/**
 * A selectable option.
 */

export type MultiSelectFilterOption = { value: string; label: React.ReactNode };

/**
 * `MultiSelectFilterProps` type.
 */

export type MultiSelectFilterProps = {
  options: MultiSelectFilterOption[];
  /** Currently selected values. */
  value: string[];
  onChange: (value: string[]) => void;
  /** Field name shown in the trigger, e.g. "Types". */
  label: string;
  /** Trigger text when every option is selected (default "All"). */
  allLabel?: string;
  /** Trigger text when nothing is selected (default "None"). */
  noneLabel?: string;
  /** Summary for a partial selection, e.g. `(n, total) => "3 of 9"`. */
  summary?: (selected: number, total: number) => string;
  selectAllLabel?: string;
  clearLabel?: string;
  triggerClassName?: string;
};

/**
 * Export `MultiSelectFilter` component.
 *
 * A compact checklist filter on a Popover: the trigger stays short (a label plus
 * an "all / n of total / none" summary) instead of spilling a chip per choice,
 * and the dropdown offers per-option checkboxes with select-all / clear.
 */

export function MultiSelectFilter({
  options,
  value,
  onChange,
  label,
  allLabel = 'All',
  noneLabel = 'None',
  summary = (selected, total) => `${selected} / ${total}`,
  selectAllLabel = 'Select all',
  clearLabel = 'Clear',
  triggerClassName
}: MultiSelectFilterProps): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const selected = React.useMemo(() => new Set(value), [value]);
  const total = options.length;

  const summaryText =
    selected.size >= total && total > 0 ? allLabel : selected.size === 0 ? noneLabel : summary(selected.size, total);

  const toggle = (optionValue: string) =>
    onChange(selected.has(optionValue) ? value.filter(item => item !== optionValue) : [...value, optionValue]);

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'group flex h-10 min-h-10 w-full items-center justify-between gap-1.5 rounded-md border border-input bg-background px-3 text-sm shadow-sm outline-none transition-colors data-[state=open]:border-ring/40',
            triggerClassName
          )}
          type={'button'}
        >
          <span className={'min-w-0 flex-1 truncate text-left'}>
            <span className={'text-muted-foreground'}>
              {label}
              {': '}
            </span>

            <span className={'font-medium text-foreground'}>{summaryText}</span>
          </span>

          <ChevronDown className={'h-4 w-4 shrink-0 opacity-50'} />
        </button>
      </PopoverTrigger>

      <PopoverContent align={'start'} className={'w-[--radix-popover-trigger-width] min-w-[200px] p-0'}>
        <div className={'flex items-center justify-between border-b border-border px-2 py-1.5 text-xs'}>
          <button
            className={'font-medium text-primary transition-colors hover:text-primary/80 disabled:opacity-40'}
            disabled={selected.size >= total}
            onClick={() => onChange(options.map(option => option.value))}
            type={'button'}
          >
            {selectAllLabel}
          </button>

          <button
            className={'font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40'}
            disabled={selected.size === 0}
            onClick={() => onChange([])}
            type={'button'}
          >
            {clearLabel}
          </button>
        </div>

        <div className={'max-h-64 overflow-y-auto p-1'}>
          {options.map(option => {
            const checked = selected.has(option.value);

            return (
              <button
                className={
                  'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-muted focus-visible:bg-muted'
                }
                key={option.value}
                onClick={() => toggle(option.value)}
                type={'button'}
              >
                <span
                  className={cn(
                    'grid h-4 w-4 shrink-0 place-items-center rounded-sm border',
                    checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
                  )}
                >
                  {checked && <Check className={'h-3 w-3'} />}
                </span>

                <span className={'min-w-0 flex-1 truncate'}>{option.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
